import type {
  MatchHandicapCalculation,
  MatchScoringParticipant,
  TournamentMatch,
} from '../api-types';
import {
  sideStrokeAllocationSummary,
  strokeAllocationSummary,
  type StrokeAllocationSummary,
} from './scoreEntryPops';

export interface PlayerHandicapExplanation {
  key: string;
  label: string;
  sideNumber: number;
  handicapIndex: number | null;
  courseHandicap: number | null;
  playingHandicap: number | null;
  usesIndexOverride: boolean;
  usesCourseOverride: boolean;
  usesPlayingOverride: boolean;
  rawMatchStrokes: number | null;
  pops: StrokeAllocationSummary;
}

export interface TeamHandicapExplanation {
  key: string;
  sideNumber: number;
  playingHandicap: number | null;
  usesTeamOverride: boolean;
  rawMatchStrokes: number | null;
  pops: StrokeAllocationSummary;
  players: PlayerHandicapExplanation[];
}

export interface MatchHandicapExplanation {
  isGross: boolean;
  isTeamFormat: boolean;
  settings: MatchHandicapCalculation | null;
  players: PlayerHandicapExplanation[];
  teams: TeamHandicapExplanation[];
}

function oneDecimal(value: number): number {
  return Math.round(value * 10) / 10;
}

export function isOneScoreTeamFormat(formatType: TournamentMatch['formatType']): boolean {
  return formatType === 'scramble_match_play'
    || formatType === 'foursomes_alternate_shot';
}

export function buildMatchHandicapExplanation({
  match,
  participants,
  calculation,
}: {
  match: TournamentMatch;
  participants: MatchScoringParticipant[];
  calculation?: MatchHandicapCalculation;
}): MatchHandicapExplanation {
  const settings = calculation ?? null;
  const baseline = settings?.baselinePlayingHandicap ?? null;
  const players = participants.map((participant): PlayerHandicapExplanation => {
    const handicapIndex = participant.handicapIndexOverride
      ?? participant.eventHandicapIndex;
    return {
      key: participant.eventPlayerId,
      label: participant.displayName,
      sideNumber: participant.sideNumber,
      handicapIndex,
      courseHandicap: participant.courseHandicap,
      playingHandicap: participant.playingHandicap,
      usesIndexOverride: participant.handicapIndexOverride != null,
      usesCourseOverride: participant.courseHandicapOverride != null,
      usesPlayingOverride: participant.playingHandicapOverride != null,
      rawMatchStrokes: baseline == null || participant.playingHandicap == null
        ? null
        : oneDecimal(participant.playingHandicap - baseline),
      pops: strokeAllocationSummary(participant.receivesStrokesJson),
    };
  });
  const teamFormat = isOneScoreTeamFormat(match.formatType);
  const teams = teamFormat
    ? ([1, 2] as const).map((sideNumber): TeamHandicapExplanation => {
        const playingHandicap = sideNumber === 1
          ? match.teamAPlayingHandicap
          : match.teamBPlayingHandicap;
        const teamOverride = sideNumber === 1
          ? match.teamAHandicapOverride
          : match.teamBHandicapOverride;
        return {
          key: `side-${sideNumber}`,
          sideNumber,
          playingHandicap,
          usesTeamOverride: teamOverride != null,
          rawMatchStrokes: baseline == null || playingHandicap == null
            ? null
            : oneDecimal(playingHandicap - baseline),
          pops: sideStrokeAllocationSummary(participants, sideNumber),
          players: players.filter((player) => player.sideNumber === sideNumber),
        };
      })
    : [];

  return {
    isGross: match.scoringMode === 'gross',
    isTeamFormat: teamFormat,
    settings,
    players,
    teams,
  };
}
